import React, { useRef, useState } from 'react';
import { useAutoControlled } from '../../hooks';
import { invoke } from '../../utils';
import handleValidation from './formValidation';
import { LoginFormProps } from './loginForm';

export interface FormValues {
    username?: string;
    usernameDirty?: boolean;
    password?: string;
    passwordDirty?: boolean;
}
export interface FormErrors {
    username?: {
        text: string;
        type: 'neutral' | 'success' | 'warning' | 'error';
    };
    password?: {
        text: string;
        type: 'neutral' | 'success' | 'warning' | 'error';
    };
}

export interface LoginFormOptions {
    initialValues?: FormValues;
    values?: FormValues;
    labelPassword?: string;
    labelUsername?: string;
    labelLoginButton?: string;
    onValidate?: (values: FormValues) => FormErrors;
}

export function useForm(options: LoginFormOptions = {}): LoginFormProps {
    const {
        labelUsername = 'Username',
        labelLoginButton = 'Login',
        labelPassword = 'Password',
        onValidate = handleValidation,
    } = options;
    const [values, setValues] = useAutoControlled({
        initialValue: options.initialValues,
        defaultValue: {
            username: '',
            usernameDirty: false,
            password: '',
            passwordDirty: false,
        },
        value: options.values,
    });

    const [errors, setErrors] = useState<FormErrors>();
    const safeOptions = useRef<LoginFormOptions>();
    const [dirty, setDirty] = useState(false);

    React.useEffect(() => {
        safeOptions.current = options;
    });

    React.useEffect(() => {
        const validationResult = onValidate(values);
        setErrors(validationResult);
    }, [values, onValidate]);

    const handleUsernameChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const username = ev.target.value;
            setValues(v => ({ ...v, username, usernameDirty: true }));
            setDirty(true);
        },
        [setValues],
    );

    const handlePasswordChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const password = ev.target.value;
            setValues(v => ({ ...v, password, passwordDirty: true }));
            setDirty(true);
        },
        [setValues],
    );

    const handleSubmit = React.useCallback((event: React.SyntheticEvent) => {
        event.preventDefault();
    }, []);

    const usernameField = React.useMemo(
        () => ({
            id: 'login-username',
            label: labelUsername,
            onChange: handleUsernameChange,
        }),
        [handleUsernameChange, labelUsername],
    );

    const passwordField = React.useMemo(
        () => ({
            id: 'login-password',
            label: labelPassword,
            onChange: handlePasswordChange,
        }),
        [handlePasswordChange, labelPassword],
    );

    const loginButton = React.useMemo(() => {
        return {
            onClick: handleSubmit,
            label: labelLoginButton,
            disabled: !dirty,
        };
    }, [handleSubmit, labelLoginButton, dirty]);

    return {
        values,
        onSubmit: handleSubmit,
        errors,
        usernameField,
        passwordField,
        loginButton,
    };
}
